/**
 * ServiceNudge - Reusable nudge component for contextual service prompts
 * Used across all job category pages
 */
import React from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { X, ArrowRight, Sparkles } from 'lucide-react';

/**
 * Floating Nudge Banner - Shows at top of listings
 */
export const NudgeBanner = ({ nudge, onDismiss, onAction }) => {
  if (!nudge) return null;
  
  return (
    <div 
      className="relative flex items-center justify-between p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border border-blue-100 shadow-sm animate-in fade-in slide-in-from-top-2 duration-300"
      data-testid="nudge-banner"
    >
      <div className="flex items-center gap-3">
        <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
          <span className="text-2xl">{nudge.icon}</span>
        </div>
        <div>
          <p className="font-semibold text-gray-900">{nudge.message}</p>
          <p className="text-sm text-gray-500">{nudge.subtitle}</p>
        </div>
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        <Button onClick={onAction} className="gap-1">
          {nudge.cta}
          <ArrowRight className="w-4 h-4" />
        </Button>
        <button 
          onClick={onDismiss}
          className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          aria-label="Dismiss"
        >
          <X className="w-4 h-4 text-gray-400" />
        </button>
      </div>
    </div>
  );
};

/**
 * Inline Nudge Card - Shows between job listings
 */
export const InlineNudgeCard = ({ nudge, onDismiss, onAction }) => {
  if (!nudge) return null;
  
  return (
    <Card 
      className="border-blue-200 bg-gradient-to-r from-blue-50 to-indigo-50 overflow-hidden"
      data-testid="inline-nudge-card"
    >
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="font-medium text-gray-900">{nudge.message}</p>
              <p className="text-sm text-gray-500">{nudge.subtitle}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button size="sm" onClick={onAction}>
              {nudge.cta}
            </Button>
            <button 
              onClick={onDismiss}
              className="p-1 hover:bg-white/50 rounded-full transition-colors"
              aria-label="Dismiss"
            >
              <X className="w-4 h-4 text-gray-400" />
            </button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

/**
 * Compact Nudge Strip - For tight spaces
 */
export const NudgeStrip = ({ nudge, onAction }) => {
  if (!nudge) return null;
  
  return (
    <button 
      onClick={onAction}
      className="w-full flex items-center justify-between p-3 bg-gradient-to-r from-amber-50 to-orange-50 rounded-lg border border-amber-200 hover:shadow-md transition-all group"
      data-testid="nudge-strip"
    >
      <div className="flex items-center gap-2">
        <span className="text-lg">{nudge.icon}</span>
        <span className="text-sm font-medium text-amber-800">{nudge.message}</span>
      </div>
      <div className="flex items-center gap-1 text-sm text-amber-600">
        <span>From £{nudge.price}</span>
        <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
      </div>
    </button>
  );
};

/**
 * Toast-style Nudge - Appears in corner
 */
export const NudgeToast = ({ nudge, onDismiss, onAction }) => {
  if (!nudge) return null;
  
  return (
    <div 
      className="fixed bottom-20 right-4 z-50 w-80 bg-white rounded-xl shadow-2xl border border-gray-200 overflow-hidden animate-in slide-in-from-right-full duration-300"
      data-testid="nudge-toast"
    >
      <div className="p-4">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
            <span className="text-xl">{nudge.icon}</span>
          </div>
          <div className="flex-1">
            <p className="font-semibold text-gray-900 text-sm">{nudge.message}</p>
            <p className="text-xs text-gray-500 mt-0.5">{nudge.subtitle}</p>
          </div>
          <button 
            onClick={onDismiss}
            className="p-1 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X className="w-4 h-4 text-gray-400" />
          </button>
        </div>
      </div>
      <div className="flex border-t">
        <button 
          onClick={onDismiss}
          className="flex-1 py-2 text-sm text-gray-500 hover:bg-gray-50 transition-colors"
        >
          Not now
        </button>
        <button 
          onClick={onAction}
          className="flex-1 py-2 text-sm font-medium text-blue-600 hover:bg-blue-50 transition-colors border-l"
        >
          {nudge.cta} →
        </button>
      </div>
    </div>
  );
};

/**
 * Sidebar Nudge Card - For job detail pages
 */
export const SidebarNudgeCard = ({ nudge, onAction, variant = 'default' }) => {
  if (!nudge) return null;
  
  const variants = {
    default: 'bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200',
    success: 'bg-gradient-to-br from-emerald-50 to-teal-50 border-emerald-200',
    warning: 'bg-gradient-to-br from-amber-50 to-orange-50 border-amber-200',
  };
  
  return (
    <Card className={`${variants[variant]} overflow-hidden`} data-testid="sidebar-nudge">
      <CardContent className="p-4">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-10 h-10 bg-white/80 rounded-full flex items-center justify-center shadow-sm">
            <span className="text-xl">{nudge.icon}</span>
          </div>
          <div>
            <p className="font-semibold text-gray-900 text-sm">{nudge.message}</p>
            <p className="text-xs text-gray-500">{nudge.subtitle}</p>
          </div>
        </div>
        <Button onClick={onAction} size="sm" className="w-full">
          {nudge.cta}
          <ArrowRight className="w-4 h-4 ml-1" />
        </Button>
      </CardContent>
    </Card>
  );
};

export default NudgeBanner;
